import React, { useCallback } from "react";
import type { JSX } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import ColorPhaseScore from "@/pages/session-detail/components/color-phase-score/color-phase-score";
import MapDisplay from "@/pages/session-detail/components/map-display/map-display";
import ResponseTime from "@/pages/session-detail/components/response-time/response-time";
import Results from "@/pages/session-detail/components/results/results";

import {
    ArrowIconWrapper,
    PopUp,
    PopUpListItem,
    Toggle,
} from "@/components/filters/filters.styles";
import { ChevronDownSquareCornersIcon24, TargetIcon20, TargetIcon24 } from "@/components/icons";
import PageHeader from "@/components/ui/page-header/page-header";

import { DropdownFilterVariant } from "@/types/filters";

import {
    CommunicationTimelineWrapper,
    ComponentsScrollableWrapper,
    ModalContentWrapperForScrollableContent,
    PhaseScoreAndFireMissionWrapper,
    PhaseScoringWrapper,
    RightContentWrapperDiv,
} from "../../modal-contents.styles";

interface FireMissionModalContentTypes {
    scrollableContainerHeight: number;
    logoUrl: string;
    getSessionSubtitleDetails: JSX.Element;
    subDimensionsData: any;
}

const FireMissionModalContent: React.FC<FireMissionModalContentTypes> = ({
    scrollableContainerHeight,
    logoUrl,
    getSessionSubtitleDetails,
    subDimensionsData,
}) => {
    const {
        isFireMissionFilterOpen,
        setIsFireMissionFilterOpen,
        fireMissionFilterOptions,
        selectedFireMission,
        setSelectedFireMission,
        selectedFireMissionIndex,
        setSelectedFireMissionIndex,
    } = useSessionDetailsContext();

    const clickOutsideRef = useDetectClickOutside({
        onTriggered: () => setIsFireMissionFilterOpen(false),
    });

    //map each subDimension to selected Dimension
    const mapFireMissionIdToSelectedFireMission = useCallback(
        (selectedFireMission: string | null) => {
            return subDimensionsData?.find((fireMission: any) => {
                return fireMission?.id === selectedFireMission;
            });
        },
        [selectedFireMission, subDimensionsData],
    );

    //toggle filters button
    const handleFilterButtonToggle = useCallback(
        (event: any) => {
            event.stopPropagation();

            setIsFireMissionFilterOpen(!isFireMissionFilterOpen);
        },
        [isFireMissionFilterOpen],
    );

    //handles filter option click
    const handleFilterOptionClick = (event: any, index: number, option: any) => {
        event.stopPropagation();
        setSelectedFireMissionIndex(index);
        setSelectedFireMission(option);
        setIsFireMissionFilterOpen(false); //to close filters when option is selected
    };

    return (
        <ModalContentWrapperForScrollableContent>
            <PageHeader
                title={selectedFireMission || "Fire Mission"}
                logoUrl={logoUrl}
                subtitleDetailText={getSessionSubtitleDetails}
                rightContent={
                    <RightContentWrapperDiv ref={clickOutsideRef}>
                        <Toggle
                            isOpen={isFireMissionFilterOpen}
                            onClick={handleFilterButtonToggle}
                            variant={DropdownFilterVariant.Primary}
                        >
                            <TargetIcon24 />
                            {selectedFireMission}
                            <ArrowIconWrapper isOpen={isFireMissionFilterOpen}>
                                <ChevronDownSquareCornersIcon24 />
                            </ArrowIconWrapper>
                        </Toggle>

                        {isFireMissionFilterOpen && (
                            <PopUp
                                id="modal-filters-popup-id"
                                variant={DropdownFilterVariant.Primary}
                                popUpWidth="16.125rem"
                            >
                                {fireMissionFilterOptions?.map((option: string, index: number) => {
                                    return (
                                        <PopUpListItem
                                            variant={DropdownFilterVariant.Primary}
                                            isSelected={selectedFireMissionIndex === index}
                                            key={`${option}-${index}`}
                                            onClick={(event) => {
                                                handleFilterOptionClick(event, index, option);
                                            }}
                                        >
                                            {option}
                                        </PopUpListItem>
                                    );
                                })}
                            </PopUp>
                        )}
                    </RightContentWrapperDiv>
                }
            />

            <ComponentsScrollableWrapper //id is needed to calculate the scrollable container's height
                id="components-scrollable-wrapper"
                scrollableContainerHeight={scrollableContainerHeight}
            >
                <PhaseScoreAndFireMissionWrapper>
                    <PhaseScoringWrapper>
                        <ColorPhaseScore
                            subDimensionData={
                                mapFireMissionIdToSelectedFireMission(selectedFireMission)
                                    ?.dataset?.["phase-scoring"]
                            }
                        />
                    </PhaseScoringWrapper>

                    <CommunicationTimelineWrapper>
                        <ResponseTime
                            subDimensionData={
                                mapFireMissionIdToSelectedFireMission(selectedFireMission)
                                    ?.dataset?.["combined-custom-charts"]
                            }
                            showYAxisLabel={false}
                            renderDivider
                            componentContainerGap="var(--spacing-4)"
                            hideOverflowY
                        />
                    </CommunicationTimelineWrapper>
                </PhaseScoreAndFireMissionWrapper>

                <Results
                    subDimensionData={
                        mapFireMissionIdToSelectedFireMission(selectedFireMission)?.dataset?.results
                    }
                    showQuestionIcons={false}
                    showSubTitle={false}
                    filterIcon={<TargetIcon20 width={20} height={20} />}
                />

                <MapDisplay
                    subDimensionData={
                        mapFireMissionIdToSelectedFireMission(selectedFireMission)?.dataset?.Map
                    }
                />
            </ComponentsScrollableWrapper>
        </ModalContentWrapperForScrollableContent>
    );
};

export default FireMissionModalContent;
